/**
 * Sensitivity sweeps and customer cash trajectory for the Venue pricing explorer.
 *
 * Each sweep holds the current quote fixed and varies one numeric input so you can
 * see the shape of Watchly capital, break-even, net profit, and customer costs.
 * Customer cash over time is split into one-time charges (package, hardware purchase,
 * install trips) versus subscription (lease and software), using the same conversion
 * credit order as `computeVenuePricing`.
 */

import {
    CONTRACT_TERM_MONTHS,
    DEFAULT_VENUE_PRICING,
    computeVenuePricing,
    type BreakEven,
    type VenuePricingBreakdown,
    type VenuePricingParams,
} from './venuePricing';

export type SweepableVenueParam =
    | 'deviceHardwareCost'
    | 'deviceInstallationLaborCost'
    | 'hardwareLeasePerMonth'
    | 'hardwarePurchase'
    | 'installationFeePerTrip'
    | 'softwareMonthly'
    | 'softwareYearly'
    | 'hardwareDiscountFraction'
    | 'installationDiscountFraction'
    | 'softwareDiscountFraction'
    | 'pilotDurationDays'
    | 'pilotDeviceCount'
    | 'pilotPackagePrice'
    | 'conversionCredit'
    | 'goForwardDeviceCount'
    | 'additionalGoForwardLocations';

export type VenueSweepOutcome =
    | 'upFrontCapital'
    | 'breakEvenMonths'
    | 'netProfitYear1'
    | 'netProfitYear2'
    | 'customerUpFront'
    | 'customerMrr';

export type SweepParamUnit = 'usd' | 'pct' | 'count' | 'days';

export type VenueSweepMetrics = {
    upFrontCapital: number;
    breakEvenMonths: number | null;
    netProfitYear1: number;
    netProfitYear2: number;
    customerUpFront: number;
    customerMrr: number;
    customerSignup: number;
    /** Null when there is no conversion invoice (steady-state). */
    customerConversion: number | null;
    yearOneCustomerTotal: number;
    yearTwoCustomerTotal: number;
};

/** Outcomes that can take optional min/max bookends. */
export type BoundableOutcome =
    | 'customerSignup'
    | 'customerConversion'
    | 'customerMrr'
    | 'yearOneCustomerTotal'
    | 'yearTwoCustomerTotal'
    | 'upFrontCapital'
    | 'breakEvenMonths'
    | 'netProfitYear1'
    | 'netProfitYear2';

export type BoundRange = {
    min?: number;
    max?: number;
};

export type OutputBounds = Partial<Record<BoundableOutcome, BoundRange>>;

export type FeasibleRange = { from: number; to: number };

export type FeasibleInputSet = {
    feasibleValues: number[];
    feasibleRanges: FeasibleRange[];
    infeasibleRanges: FeasibleRange[];
    kind: 'unconstrained' | 'all' | 'single' | 'union' | 'empty';
    /** Present only when `kind === 'single'` — outer sample span of that one interval. */
    clamp: { min: number; max: number } | null;
};

export type VenueSweepPoint = VenueSweepMetrics & {
    inputValue: number;
};

export type VenueParamSweep = {
    key: SweepableVenueParam;
    label: string;
    unit: SweepParamUnit;
    current: number;
    points: VenueSweepPoint[];
};

export type CustomerCashPoint = {
    month: number;
    oneTime: number;
    subscription: number;
    cumulativeOneTime: number;
    cumulativeSubscription: number;
    cumulativeTotal: number;
};

export const CUSTOMER_CASH_HORIZON_MONTHS = 24;

export const SWEEP_PARAM_META: Record<
    SweepableVenueParam,
    { label: string; unit: SweepParamUnit }
> = {
    deviceHardwareCost: { label: 'Device hardware cost to Watchly', unit: 'usd' },
    deviceInstallationLaborCost: { label: 'Installation labor per device', unit: 'usd' },
    hardwareLeasePerMonth: { label: 'Hardware lease per month', unit: 'usd' },
    hardwarePurchase: { label: 'Hardware purchase per device', unit: 'usd' },
    installationFeePerTrip: { label: 'Installation fee per trip', unit: 'usd' },
    softwareMonthly: { label: 'Software per device / month', unit: 'usd' },
    softwareYearly: { label: 'Software per device / year', unit: 'usd' },
    hardwareDiscountFraction: { label: 'Discount on hardware', unit: 'pct' },
    installationDiscountFraction: { label: 'Discount on installation', unit: 'pct' },
    softwareDiscountFraction: { label: 'Discount on software', unit: 'pct' },
    pilotDurationDays: { label: 'Pilot duration (days)', unit: 'days' },
    pilotDeviceCount: { label: 'Pilot device count', unit: 'count' },
    pilotPackagePrice: { label: 'Pilot package price', unit: 'usd' },
    conversionCredit: { label: 'Conversion credit', unit: 'usd' },
    goForwardDeviceCount: { label: 'Go-forward device count', unit: 'count' },
    additionalGoForwardLocations: { label: 'Additional go-forward locations', unit: 'count' },
};

export const SWEEP_OUTCOME_META: Record<
    VenueSweepOutcome,
    { label: string; shortLabel: string; unit: 'usd' | 'months' }
> = {
    upFrontCapital: {
        label: 'Capital tied up post-pilot (unclamped)',
        shortLabel: 'Capital',
        unit: 'usd',
    },
    breakEvenMonths: {
        label: 'Time to break even from start',
        shortLabel: 'Break-even',
        unit: 'months',
    },
    netProfitYear1: {
        label: 'Watchly net profit after 1 year',
        shortLabel: 'Net Y1',
        unit: 'usd',
    },
    netProfitYear2: {
        label: 'Watchly net profit after 2 years',
        shortLabel: 'Net Y2',
        unit: 'usd',
    },
    customerUpFront: {
        label: 'Customer cash due at signup + conversion',
        shortLabel: 'Cust. upfront',
        unit: 'usd',
    },
    customerMrr: {
        label: 'Customer monthly recurring (MRR)',
        shortLabel: 'Cust. MRR',
        unit: 'usd',
    },
};

export const BOUND_OUTCOME_META: Record<
    BoundableOutcome,
    { label: string; group: 'customer' | 'watchly'; unit: 'usd' | 'months' }
> = {
    customerSignup: { label: 'Signup invoice', group: 'customer', unit: 'usd' },
    customerConversion: { label: 'Conversion invoice', group: 'customer', unit: 'usd' },
    customerMrr: { label: 'Ongoing MRR', group: 'customer', unit: 'usd' },
    yearOneCustomerTotal: { label: 'Year-one customer total', group: 'customer', unit: 'usd' },
    yearTwoCustomerTotal: { label: 'Year-two customer total', group: 'customer', unit: 'usd' },
    upFrontCapital: { label: 'Capital tied up post-pilot', group: 'watchly', unit: 'usd' },
    breakEvenMonths: { label: 'Months to break even', group: 'watchly', unit: 'months' },
    netProfitYear1: { label: 'Watchly net profit after 1 year', group: 'watchly', unit: 'usd' },
    netProfitYear2: { label: 'Watchly net profit after 2 years', group: 'watchly', unit: 'usd' },
};

export const BOUNDABLE_OUTCOMES: BoundableOutcome[] = [
    'customerSignup',
    'customerConversion',
    'customerMrr',
    'yearOneCustomerTotal',
    'yearTwoCustomerTotal',
    'upFrontCapital',
    'breakEvenMonths',
    'netProfitYear1',
    'netProfitYear2',
];

type SweepDomain = {
    min: number;
    max: number;
    step: number;
    integer?: boolean;
};

const SWEEP_DOMAINS: Record<SweepableVenueParam, SweepDomain> = {
    deviceHardwareCost: { min: 0, max: 4000, step: 200 },
    deviceInstallationLaborCost: { min: 0, max: 1000, step: 50 },
    hardwareLeasePerMonth: { min: 0, max: 200, step: 10 },
    hardwarePurchase: { min: 0, max: 2500, step: 125 },
    installationFeePerTrip: { min: 0, max: 2500, step: 125 },
    softwareMonthly: { min: 0, max: 100, step: 5 },
    softwareYearly: { min: 0, max: 1200, step: 60 },
    hardwareDiscountFraction: { min: 0, max: 1, step: 0.05 },
    installationDiscountFraction: { min: 0, max: 1, step: 0.05 },
    softwareDiscountFraction: { min: 0, max: 1, step: 0.05 },
    pilotDurationDays: { min: 0, max: 180, step: 9, integer: true },
    pilotDeviceCount: { min: 1, max: 12, step: 1, integer: true },
    pilotPackagePrice: { min: 0, max: 8000, step: 400 },
    conversionCredit: { min: 0, max: 8000, step: 400 },
    goForwardDeviceCount: { min: 1, max: 20, step: 1, integer: true },
    additionalGoForwardLocations: { min: 0, max: 8, step: 1, integer: true },
};

const ALL_SWEEP_KEYS = Object.keys(SWEEP_PARAM_META) as SweepableVenueParam[];

function money(n: number): number {
    return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

function roundToStep(n: number, step: number, integer?: boolean): number {
    const r = Math.round(n / step) * step;
    if (integer) return Math.round(r);
    if (step < 1) return Math.round(r * 1000) / 1000;
    return money(r);
}

export function breakEvenToMonths(b: BreakEven): number | null {
    if (b.kind === 'immediate') return 0;
    if (b.kind === 'never') return null;
    return b.monthsAfterStart;
}

export function unclampedUpFrontCapital(b: VenuePricingBreakdown): number {
    const offsetting = money(
        b.pilotPackageDue +
            b.conversionAfterCredit.install +
            b.conversionAfterCredit.hardwarePurchase +
            b.expansionInstallTripDue
    );
    return money(b.grossDeploySpend - offsetting);
}

export function netProfitAtMonth(b: VenuePricingBreakdown, month: number): number {
    const hit = b.watchlyNetProfitSeries.find(p => p.month === month);
    if (hit) return hit.netProfit;
    const last = b.watchlyNetProfitSeries[b.watchlyNetProfitSeries.length - 1];
    return last?.netProfit ?? 0;
}

export function metricsFromBreakdown(b: VenuePricingBreakdown): VenueSweepMetrics {
    return {
        upFrontCapital: unclampedUpFrontCapital(b),
        breakEvenMonths: breakEvenToMonths(b.breakEvenFromStart),
        netProfitYear1: netProfitAtMonth(b, 12),
        netProfitYear2: netProfitAtMonth(b, 24),
        customerUpFront: money(b.invoices.signup.total + (b.invoices.conversion?.total ?? 0)),
        customerMrr: b.mrr,
        customerSignup: b.invoices.signup.total,
        customerConversion: b.invoices.conversion ? b.invoices.conversion.total : null,
        yearOneCustomerTotal: b.yearOneCustomerTotal,
        yearTwoCustomerTotal: b.yearTwoCustomerTotal,
    };
}

export function outcomeValue(metrics: VenueSweepMetrics, outcome: VenueSweepOutcome): number | null {
    return metrics[outcome];
}

export function boundMetricValue(metrics: VenueSweepMetrics, outcome: BoundableOutcome): number | null {
    return metrics[outcome];
}

function rangeIsActive(range: BoundRange | undefined): boolean {
    if (!range) return false;
    return range.min != null || range.max != null;
}

export function hasActiveBounds(bounds: OutputBounds): boolean {
    return BOUNDABLE_OUTCOMES.some(key => rangeIsActive(bounds[key]));
}

/**
 * Whether one quote’s metrics sit inside every active min/max bookend.
 *
 * Break-even “never” fails any bound on months (it is outside a finite window).
 * A missing conversion invoice (steady-state) skips the conversion bound rather
 * than failing it.
 */
export function pointHonorsBounds(metrics: VenueSweepMetrics, bounds: OutputBounds): boolean {
    return failedBounds(metrics, bounds).length === 0;
}

export function failedBounds(metrics: VenueSweepMetrics, bounds: OutputBounds): BoundableOutcome[] {
    const failed: BoundableOutcome[] = [];
    for (const key of BOUNDABLE_OUTCOMES) {
        const range = bounds[key];
        if (!rangeIsActive(range)) continue;
        const value = boundMetricValue(metrics, key);
        if (key === 'customerConversion' && value == null) continue;
        if (value == null) {
            failed.push(key);
            continue;
        }
        if (range!.min != null && value < range!.min) failed.push(key);
        else if (range!.max != null && value > range!.max) failed.push(key);
    }
    return failed;
}

function contiguousRuns(
    points: { x: number; ok: boolean }[]
): { from: number; to: number; ok: boolean }[] {
    if (points.length === 0) return [];
    const runs: { from: number; to: number; ok: boolean }[] = [];
    let run = { from: points[0].x, to: points[0].x, ok: points[0].ok };
    for (let i = 1; i < points.length; i++) {
        const p = points[i];
        if (p.ok === run.ok) {
            run.to = p.x;
        } else {
            runs.push(run);
            run = { from: p.x, to: p.x, ok: p.ok };
        }
    }
    runs.push(run);
    return runs;
}

export function feasibleInputSet(sweep: VenueParamSweep, bounds: OutputBounds): FeasibleInputSet {
    if (!hasActiveBounds(bounds)) {
        return {
            feasibleValues: sweep.points.map(p => p.inputValue),
            feasibleRanges: [],
            infeasibleRanges: [],
            kind: 'unconstrained',
            clamp: null,
        };
    }

    const flagged = sweep.points.map(p => ({
        x: p.inputValue,
        ok: pointHonorsBounds(p, bounds),
    }));
    const runs = contiguousRuns(flagged);
    const feasibleRanges = runs.filter(r => r.ok).map(({ from, to }) => ({ from, to }));
    const infeasibleRanges = runs.filter(r => !r.ok).map(({ from, to }) => ({ from, to }));
    const feasibleValues = flagged.filter(p => p.ok).map(p => p.x);

    if (feasibleValues.length === 0) {
        return {
            feasibleValues,
            feasibleRanges,
            infeasibleRanges,
            kind: 'empty',
            clamp: null,
        };
    }
    if (infeasibleRanges.length === 0) {
        return {
            feasibleValues,
            feasibleRanges,
            infeasibleRanges,
            kind: 'all',
            clamp: null,
        };
    }
    if (feasibleRanges.length === 1) {
        const only = feasibleRanges[0];
        return {
            feasibleValues,
            feasibleRanges,
            infeasibleRanges,
            kind: 'single',
            clamp: { min: only.from, max: only.to },
        };
    }
    return {
        feasibleValues,
        feasibleRanges,
        infeasibleRanges,
        kind: 'union',
        clamp: null,
    };
}

/**
 * Numeric inputs that actually apply to this quote. Lease vs purchase and monthly vs
 * yearly software hide the unused price field so the overview is not full of flat charts.
 */
export function activeSweepParams(params: VenuePricingParams): SweepableVenueParam[] {
    return ALL_SWEEP_KEYS.filter(key => {
        if (key === 'hardwareLeasePerMonth' && params.hardwareMode !== 'lease') return false;
        if (key === 'hardwarePurchase' && params.hardwareMode !== 'purchase') return false;
        if (key === 'softwareMonthly' && params.softwareCadence !== 'month') return false;
        if (key === 'softwareYearly' && params.softwareCadence !== 'year') return false;
        if (params.pilotDurationDays <= 0) {
            if (key === 'pilotPackagePrice' || key === 'conversionCredit' || key === 'pilotDurationDays') {
                return key === 'pilotDurationDays';
            }
        }
        return true;
    });
}

export function sampleValuesForParam(key: SweepableVenueParam, params: VenuePricingParams): number[] {
    const domain = SWEEP_DOMAINS[key];
    let min = domain.min;
    let max = domain.max;
    if (key === 'goForwardDeviceCount') {
        min = Math.max(params.pilotDeviceCount, 1);
        max = Math.max(max, min);
    }

    const stepCount = Math.round((max - min) / domain.step);
    const values: number[] = [];
    for (let i = 0; i <= stepCount; i++) {
        values.push(roundToStep(min + i * domain.step, domain.step, domain.integer));
    }

    const current = params[key];
    if (Number.isFinite(current) && current >= min && current <= max) {
        values.push(roundToStep(current, domain.integer ? 1 : domain.step, domain.integer));
        values.push(domain.integer ? Math.round(current) : money(current));
    }

    const unique = Array.from(new Set(values.map(n => (domain.integer ? Math.round(n) : money(n)))));
    unique.sort((a, b) => a - b);
    return unique;
}

export function sweepVenueParam(
    params: VenuePricingParams,
    key: SweepableVenueParam
): VenueParamSweep {
    const meta = SWEEP_PARAM_META[key];
    const samples = sampleValuesForParam(key, params);
    const points: VenueSweepPoint[] = samples.map(inputValue => {
        const next: VenuePricingParams = { ...params, [key]: inputValue };
        const breakdown = computeVenuePricing(next);
        return { inputValue, ...metricsFromBreakdown(breakdown) };
    });

    return {
        key,
        label: meta.label,
        unit: meta.unit,
        current: params[key],
        points,
    };
}

export function sweepActiveVenueParams(params: VenuePricingParams): VenueParamSweep[] {
    return activeSweepParams(params).map(key => sweepVenueParam(params, key));
}

/**
 * Month-by-month customer cash from install through `horizonMonths`.
 *
 * One-time: pilot package, hardware purchase after credit, install-trip fees.
 * Subscription: the 12-month lease/software term billed at conversion (after credit),
 * then the same term again at each conversion anniversary.
 */
export function computeCustomerCashSeries(
    overrides: Partial<VenuePricingParams> = {},
    horizonMonths: number = CUSTOMER_CASH_HORIZON_MONTHS
): CustomerCashPoint[] {
    const params: VenuePricingParams = { ...DEFAULT_VENUE_PRICING, ...overrides };
    const b = computeVenuePricing(params);
    const isSteadyState = params.pilotDurationDays <= 0;
    const conversionMonth = isSteadyState ? 0 : Math.ceil(params.pilotDurationDays / 30);
    const horizon = Math.max(1, Math.floor(horizonMonths));

    const packageCash = isSteadyState ? 0 : money(Math.max(0, params.pilotPackagePrice));
    const oneTimeAtConversion = money(
        b.conversionAfterCredit.install +
            b.conversionAfterCredit.hardwarePurchase +
            b.expansionInstallTripDue
    );
    const subscriptionAtConversion = money(
        b.conversionAfterCredit.firstSoftwarePeriod + b.conversionAfterCredit.firstLeaseMonth
    );
    const termRenewal = b.yearTwoCustomerTotal;

    let cumulativeOneTime = 0;
    let cumulativeSubscription = 0;
    const points: CustomerCashPoint[] = [];

    for (let m = 0; m <= horizon; m++) {
        let oneTime = 0;
        let subscription = 0;

        if (m === 0 && !isSteadyState) {
            oneTime = money(oneTime + packageCash);
        }

        if (m === conversionMonth) {
            oneTime = money(oneTime + oneTimeAtConversion);
            subscription = money(subscription + subscriptionAtConversion);
        }

        if (m > conversionMonth && m < horizon) {
            const monthsSinceConversion = m - conversionMonth;
            if (monthsSinceConversion % CONTRACT_TERM_MONTHS === 0) {
                subscription = money(subscription + termRenewal);
            }
        }

        cumulativeOneTime = money(cumulativeOneTime + oneTime);
        cumulativeSubscription = money(cumulativeSubscription + subscription);

        points.push({
            month: m,
            oneTime,
            subscription,
            cumulativeOneTime,
            cumulativeSubscription,
            cumulativeTotal: money(cumulativeOneTime + cumulativeSubscription),
        });
    }

    return points;
}
